// ── Artifact surfaces — transcript ONLY (reports REBUILT in Wave 7) ─────────
// The report library / report detail / session report surfaces moved to real
// gold-pattern pages in product-reports-surfaces.tsx (ReportsPage /
// ReportDetailPage / SessionReportPage). This file now hosts ONLY the transcript
// detail surface; the reports parts that used to live here are gone. Transcript
// access is a separate operational lane (participant + cohort_lead hard-denied
// server-side) and is left as its existing focused surface.

import { Alert, Badge, Button, DetailSurface, downloadBlob } from "@vjal/ui";
import type { DataTableColumn } from "@vjal/ui";
import { Download } from "lucide-react";
import { useState } from "react";
import { HonestState, useHonestQuery } from "../components/honest-state";
import { evaluateArtifactAccess } from "../domain/sensitive-surfaces";
import type { HonestPosture } from "../components/honest-state";
import type { CoachingRole } from "../domain/roles";
import { coachingQueryKeys } from "../hooks/coaching-query-keys";
import { getCoachingDataMode, getCoachingServices } from "../services/coaching-service-provider";
import type { HonestGate } from "../services/coaching-error-posture";
import { normalizeLiveTranscript, type LiveTranscript } from "../services/coaching-transcript";
import type { ProductScreenContract, ProductScreenContext, TableRow } from "./product-route-contracts";
import { liveRecordIdFromPath } from "./product-live-rows";

const services = getCoachingServices();

function humanize(value: string): string {
  return value.replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function safeFilenamePart(value: string): string {
  return value.replace(/[^A-Za-z0-9_-]+/g, "-") || "session";
}

export function downloadTranscriptFile(text: string, sessionId: string, redacted: boolean): string {
  const filename = `coaching-transcript-${safeFilenamePart(sessionId)}${redacted ? "-redacted" : ""}.txt`;
  downloadBlob(text, filename, "text/plain;charset=utf-8");
  return filename;
}


export function transcriptDetailScreen(): ProductScreenContract {
  return {
    title: () => "Transcript detail",
    primaryJob: "Render transcript access only for authorized operational and company-admin roles with redaction clearly shown.",
    secondaryJob: "Participants receive generic route denial before this screen renders.",
    workTitle: "Transcript authorization checkpoints",
    rows: [
      { id: "access", checkpoint: "Access", value: "Transcript scoped to visible session", state: "authorized roles only" },
      { id: "redaction", checkpoint: "Redaction", value: "Coach-private content excluded for company admin", state: "redacted" }
    ],
    columns: artifactColumns(),
    states: ["loading", "error", "denied", "redacted", "download-authorized"],
    apiFamilies: ["artifact-service", "history-service"],
    roleDeltas: "Company admin receives redacted assigned-company transcript; participant cannot access transcript route.",
    extensionSlots: ["redaction diff", "download audit", "review/approve workflow"],
    mobilePosture: "Redaction warning and download controls remain visible before detail rows.",
    evidenceRequirement: "Playwright must prove company-admin redaction and participant generic denial.",
    surfaceMode: "focused",
    customBody: TranscriptDetailSurface
  };
}

function TranscriptDetailSurface({ role, routePath }: ProductScreenContext) {
  const sessionId = liveRecordIdFromPath(routePath ?? "", "sessions");
  return getCoachingDataMode() === "live"
    ? <LiveTranscriptSurface role={role} sessionId={sessionId} />
    : <MockTranscriptSurface role={role} routePath={routePath} />;
}

function LiveTranscriptSurface({ role, sessionId }: { role: CoachingRole; sessionId: string | null }) {
  const validId = Boolean(sessionId && sessionId !== "new");
  const denied = role === "participant" || role === "cohort_lead";
  const query = useHonestQuery<LiveTranscript>(
    [...coachingQueryKeys.sessionDetail(sessionId ?? ""), "transcript"],
    async () => normalizeLiveTranscript(await services.reports.sessionTranscript(sessionId as string)),
    { enabled: validId && !denied }
  );

  if (denied) {
    return <TranscriptState posture="permission-denied" title="Transcript unavailable to your role" message="No transcript content is shown for this role." />;
  }
  if (!validId) {
    return <TranscriptState posture="empty" title="Transcript not found" message="No session transcript is available for this route." />;
  }
  if (query.posture !== null) {
    const notFound = query.posture === "empty";
    return (
      <TranscriptState
        posture={query.posture}
        gate={query.gate}
        reference={query.reference}
        onRetry={query.retry}
        title={notFound ? "Transcript not found" : undefined}
        message={notFound ? "No transcript is available for this session, or it isn't one you're authorized to see." : undefined}
      />
    );
  }

  const transcript = query.data;
  if (!transcript || !transcript.available || transcript.text.length === 0) {
    const forbidden = transcript?.artifactStatus === "forbidden" || transcript?.accessMode === "forbidden";
    return (
      <TranscriptState
        posture={forbidden ? "permission-denied" : "empty"}
        title={forbidden ? "Transcript unavailable" : "No transcript available"}
        message={forbidden ? "The authorized response does not include transcript content for this role." : "The session does not have an ingested transcript yet."}
      />
    );
  }

  return <LiveTranscriptContent role={role} sessionId={sessionId as string} transcript={transcript} />;
}

function TranscriptState({
  posture,
  gate,
  reference,
  onRetry,
  title,
  message
}: {
  posture: HonestPosture;
  gate?: HonestGate | null;
  reference?: string | null;
  onRetry?: () => void;
  title?: string;
  message?: string;
}) {
  return (
    <section aria-label="Transcript detail" className="col-span-12 flex flex-col gap-4" data-testid="transcript-detail-page">
      <div className="rounded-xl border border-line bg-white">
        <HonestState posture={posture} gate={gate} reference={reference} onRetry={onRetry} title={title} message={message} />
      </div>
    </section>
  );
}

function LiveTranscriptContent({ role, sessionId, transcript }: { role: CoachingRole; sessionId: string; transcript: LiveTranscript }) {
  const redacted = transcript.accessMode === "redacted";
  const [downloaded, setDownloaded] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  function onDownload() {
    setDownloadError(null);
    try {
      downloadTranscriptFile(transcript.text, sessionId, redacted);
      setDownloaded(true);
    } catch (cause) {
      setDownloaded(false);
      setDownloadError(cause instanceof Error && cause.message ? cause.message : "The transcript could not be downloaded. Try again.");
    }
  }

  return (
    <section aria-label="Transcript detail" className="col-span-12 flex flex-col gap-4" data-testid="transcript-detail-page">
      <DetailSurface
        title={`Session ${sessionId} transcript`}
        fields={[
          { id: "transcript", label: "Transcript", value: "Available" },
          { id: "access", label: "Access", value: humanize(transcript.accessMode) },
          { id: "redaction", label: "Redaction posture", value: humanize(transcript.redactionStatus) },
          { id: "visibility", label: "Visibility", value: humanize(transcript.visibility) }
        ]}
        actions={
          <div className="space-y-2">
            <Button type="button" variant="secondary" leftIcon={<Download size={16} />} onClick={onDownload}>
              {redacted ? "Download redacted transcript" : "Download transcript"}
            </Button>
            {downloadError ? <Alert variant="error">{downloadError}</Alert> : null}
            {downloaded ? <Alert variant="success">Transcript downloaded.</Alert> : null}
          </div>
        }
        titleExactName="Session transcript"
      >
        <div className="mt-4 space-y-4">
          <Alert variant={redacted ? "warning" : "success"}>
            {redacted ? "The API returned a redacted transcript; coach-private content is excluded." : "The API returned the transcript authorized for this operational view."}
          </Alert>
          <Badge variant={redacted ? "warning" : "success"}>
            {redacted ? "coach-private content excluded" : `${humanize(transcript.accessMode)} transcript`}
          </Badge>
          <pre className="m-0 max-h-[32rem] overflow-auto whitespace-pre-wrap rounded-lg border border-line bg-surface-subtle p-4 text-sm leading-6 text-ink" data-testid="transcript-content">
            {transcript.text}
          </pre>
          <p className="m-0 text-sm text-ink-muted">Transcript content is scoped to session {sessionId} for the {role.replace(/_/g, " ")} view.</p>
        </div>
      </DetailSurface>
    </section>
  );
}

function MockTranscriptSurface({ role, routePath }: { role: CoachingRole; routePath: string }) {
  const decision = evaluateArtifactAccess(role, "transcript");
  const redacted = decision.state === "redacted";
  const sessionId = liveRecordIdFromPath(routePath ?? "", "sessions");
  const sessionLabel = sessionId ? `Session ${sessionId} transcript` : "Session transcript";
  const [downloaded, setDownloaded] = useState(false);

  return (
    <section aria-label="Transcript detail" className="col-span-12 flex flex-col gap-4" data-testid="transcript-detail-page">
      <DetailSurface
        title={sessionLabel}
        fields={[
          { id: "transcript", label: "Transcript", value: redacted ? "redacted transcript" : "scoped transcript" },
          { id: "access", label: "Access", value: decision.label },
          { id: "download", label: "Download", value: redacted ? "redacted download only" : "authorized download" }
        ]}
        actions={
          <Button
            type="button"
            leftIcon={<Download size={16} />}
            onClick={() => {
              downloadTranscriptFile(redacted ? "redacted transcript" : "scoped transcript", sessionId ?? "session", redacted);
              setDownloaded(true);
            }}
          >
            {redacted ? "Download redacted transcript" : "Download transcript"}
          </Button>
        }
        titleExactName="Session transcript"
      >
        <div className="mt-4 space-y-4">
          <Alert variant={redacted ? "warning" : "success"}>{decision.label}</Alert>
          <Badge variant={redacted ? "warning" : "success"}>{redacted ? "coach-private content excluded" : "transcript scoped"}</Badge>
          <p className="m-0 text-sm text-ink-muted">Transcript access is scoped to the resolved session route. Participant transcript access is denied at route level.</p>
          {downloaded ? <Alert variant="success">Transcript downloaded.</Alert> : null}
        </div>
      </DetailSurface>
    </section>
  );
}

function artifactColumns(): DataTableColumn<TableRow>[] {
  return [
    { key: "checkpoint", header: "Checkpoint" },
    { key: "value", header: "Value" },
    { key: "state", header: "State", render: (row) => <Badge variant={row.state === "ready" ? "success" : "warning"}>{String(row.state)}</Badge> }
  ];
}
